import {
  ComparisonFilter,
  ListFilter,
  LogicalFilter,
} from '@sitecore-cloudsdk/search/browser';

/** Known Article Search URL query parameter keys. Extend as new filters are added. */
export const ARTICLE_SEARCH_QUERY_PARAMS = {
  expertName: 'expertName',
} as const;

export type ArticleSearchQueryParamKey =
  (typeof ARTICLE_SEARCH_QUERY_PARAMS)[keyof typeof ARTICLE_SEARCH_QUERY_PARAMS];

export type ArticleSearchQueryParams = {
  /** Expert display name from the `expertName` query parameter. */
  expertName?: string | null;
};

export type ArticleSearchFilter = LogicalFilter<'or'>;

/**
 * Decode and trim a query param value. Returns `undefined` for empty/invalid input.
 * `URLSearchParams` usually returns decoded values; this still tolerates encoded input
 * and ignores malformed percent-encoding.
 */
export function normalizeSearchQueryParam(
  value: string | string[] | null | undefined
): string | undefined {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw == null || typeof raw !== 'string') {
    return undefined;
  }

  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    decoded = raw;
  }

  const trimmed = decoded.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Default type filter when no expert (or other query) filter applies. */
export function buildDefaultArticleTypeFilter(): ArticleSearchFilter {
  return new LogicalFilter('or', [
    new ComparisonFilter('type', 'eq', 'Expert Comment'),
    new ComparisonFilter('type', 'eq', 'Press Release'),
  ]);
}

/**
 * Filter for `expertName`: Press Releases by `author_name`, Expert Comments by `author_names`.
 *
 * Produces:
 * ```
 * OR [
 *   AND [ author_name eq name, type eq "Press Release" ],
 *   AND [ author_names anyOf [name], type eq "Expert Comment" ]
 * ]
 * ```
 */
export function buildExpertNameFilter(expertName: string): ArticleSearchFilter {
  return new LogicalFilter('or', [
    new LogicalFilter('and', [
      new ComparisonFilter('author_name', 'eq', expertName),
      new ComparisonFilter('type', 'eq', 'Press Release'),
    ]),
    new LogicalFilter('and', [
      new ListFilter('author_names', 'anyOf', [expertName]),
      new ComparisonFilter('type', 'eq', 'Expert Comment'),
    ]),
  ]);
}

/**
 * Builds the Article Search widget filter from optional URL query params.
 * When `expertName` is absent/empty, returns the default type filter (unchanged behavior).
 * Add further query-param branches here as new filters are introduced.
 */
export function buildArticleSearchFilter(
  params: ArticleSearchQueryParams = {}
): ArticleSearchFilter {
  const expertName = normalizeSearchQueryParam(params.expertName);

  if (expertName) {
    return buildExpertNameFilter(expertName);
  }

  return buildDefaultArticleTypeFilter();
}
